// client/src/components/pages/AmendmentsAllocateTransactions/ShareTransactionModal.tsx
// (converted from .js, ST-00173). "Share Transaction" on the Amendments page: a ticket card
// copied to the clipboard as an image or downloaded as a PDF.
//
// Dark mode (ST-00173): the card itself has been a fixed light "paper" card since ST-00175.
// What was left: the scrim was `bg-gray-600/50`, and gray-600 flips to a light grey in dark,
// so the page behind lit up instead of dimming; and the Copy Image label was white on the
// brand teal (2.4:1), washing out to ~1.8:1 on hover because primary-700 is a light teal in
// dark. The scrim now follows the page's other modals, and the label takes dark ink in dark.
import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { FaCopy, FaFilePdf, FaCheck, FaTimes } from 'react-icons/fa';
import type { AmendmentTicketRow } from '../../../types/clientPages/amendments';

export interface ShareTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The row as the list returned it, keyed by column name. */
  transactionData: AmendmentTicketRow | null;
}

type ActionState = 'idle' | 'working' | 'success' | 'error';

/**
 * Copy Image fill and label per state. In dark, primary-600 is the brand teal #45B7AA and
 * primary-700 / emerald-700 are lighter still, so a white label fails there; `slate-50` is
 * the near-black ink in dark (white in light, so light mode is unchanged): about 7.8:1 on the
 * teal and 11:1 on its hover. Red keeps white: red-600 is the same in both themes (4.8:1).
 */
const COPY_BUTTON_TONE: Record<ActionState, string> = {
  idle: 'bg-primary-600 hover:bg-primary-700 text-white dark:text-slate-50',
  working: 'bg-primary-600 hover:bg-primary-700 text-white dark:text-slate-50',
  success: 'bg-emerald-600 hover:bg-emerald-700 text-white dark:text-slate-50',
  error: 'bg-red-600 hover:bg-red-700 text-white',
};

const formatTimestamp = (value: unknown): string => {
  if (!value) return 'N/A';
  if (typeof value === 'string') {
    if (value.includes('T')) return value.replace('T', ' ').substring(0, 19);
    return value;
  }
  const date = new Date(value as number | Date);
  if (isNaN(date.getTime())) return String(value);
  return date.toISOString().replace('T', ' ').substring(0, 19);
};

const formatValue = (value: unknown): string => {
  if (value === null || typeof value === 'undefined' || value === '') return 'N/A';
  return String(value);
};

const ShareTransactionModal = ({
  isOpen,
  onClose,
  transactionData,
}: ShareTransactionModalProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [copyState, setCopyState] = useState<ActionState>('idle');
  const [pdfState, setPdfState] = useState<ActionState>('idle');

  if (!isOpen || !transactionData) return null;

  const ticketNo = formatValue(transactionData['Ticket No']);
  const deliveryNo = formatValue(transactionData['Delivery No']);
  const regNo = formatValue(transactionData['Reg No']);
  const ticketTimestamp = formatTimestamp(transactionData['Ticket Timestamp']);
  const ticketType = formatValue(transactionData['Ticket Type']);
  const nettWeight = formatValue(transactionData['Nett Weight']);
  const dispatchLocation = formatValue(transactionData['Dispatch Location']);
  const receiptLocation = formatValue(transactionData['Receipt Location']);

  const renderCanvas = async () => {
    if (!cardRef.current) return null;
    return html2canvas(cardRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
    });
  };

  const handleCopy = async () => {
    setCopyState('working');
    try {
      const canvas = await renderCanvas();
      if (!canvas) throw new Error('Render failed');
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Blob conversion failed');
      if (!navigator.clipboard || typeof window.ClipboardItem === 'undefined') {
        throw new Error('Clipboard API unavailable in this browser');
      }
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      setCopyState('success');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2500);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfState('working');
    try {
      const canvas = await renderCanvas();
      if (!canvas) throw new Error('Render failed');
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 40;
      const maxWidth = pageWidth - margin * 2;
      const ratio = canvas.height / canvas.width;
      const imgWidth = maxWidth;
      const imgHeight = imgWidth * ratio;
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
      const safeTicket = String(ticketNo).replace(/[^a-zA-Z0-9_-]/g, '_');
      pdf.save(`transaction_${safeTicket}.pdf`);
      setPdfState('success');
      setTimeout(() => setPdfState('idle'), 2000);
    } catch (err) {
      console.error('PDF download failed:', err);
      setPdfState('error');
      setTimeout(() => setPdfState('idle'), 2500);
    }
  };

  const rows = [
    { label: 'Ticket No', value: ticketNo },
    { label: 'Delivery No', value: deliveryNo },
    { label: 'Reg No', value: regNo },
    { label: 'Ticket Timestamp', value: ticketTimestamp },
    { label: 'Type', value: ticketType },
    { label: 'Dispatch Location', value: dispatchLocation },
    { label: 'Receipt Location', value: receiptLocation },
    { label: 'Nett Weight', value: nettWeight },
  ];

  const copyLabel: string = {
    idle: 'Copy Image',
    working: 'Copying…',
    success: 'Copied!',
    error: 'Copy failed',
  }[copyState];

  const pdfLabel: string = {
    idle: 'Download PDF',
    working: 'Generating…',
    success: 'Downloaded',
    error: 'Failed',
  }[pdfState];

  const busy = copyState === 'working' || pdfState === 'working';

  return (
    <div
      data-testid="share-transaction-scrim"
      className="fixed inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center"
      onClick={onClose}
    >
      <div
        className="relative mx-auto p-6 border w-full max-w-lg shadow-lg rounded-md bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Share Transaction</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* The card is what gets copied / downloaded, and html2canvas paints it
            on white — so it is a fixed light "paper" card in BOTH themes, with
            literal colours. Theming it would make the preview differ from the
            image people receive (and in dark mode its slate text vanished). */}
        <div className="flex justify-center">
          <div
            ref={cardRef}
            data-testid="share-transaction-card"
            style={{
              width: '440px',
              padding: '24px',
              backgroundColor: '#ffffff',
              color: '#0f172a',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '4px',
              }}
            >
              Transaction
            </div>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#0f172a',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid #0f172a',
              }}
            >
              {ticketNo}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {rows.map(({ label, value }) => (
                  <tr key={label}>
                    <td
                      style={{
                        padding: '8px 0',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#475569',
                        borderBottom: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        width: '40%',
                      }}
                    >
                      {label}
                    </td>
                    <td
                      style={{
                        padding: '8px 0',
                        fontSize: '13px',
                        color: '#0f172a',
                        borderBottom: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white dark:text-slate-50 bg-slate-700 rounded-md hover:bg-slate-800 disabled:opacity-50"
          >
            <FaFilePdf className="w-3.5 h-3.5" />
            {pdfLabel}
          </button>
          <button
            onClick={handleCopy}
            disabled={busy}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 ${COPY_BUTTON_TONE[copyState]}`}
          >
            {copyState === 'success' ? (
              <FaCheck className="w-3.5 h-3.5" />
            ) : (
              <FaCopy className="w-3.5 h-3.5" />
            )}
            {copyLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareTransactionModal;
